import { useCallback, useMemo } from 'react';
import { css, useTheme, type Theme } from '@emotion/react';
import { transparentize } from 'polished';
import { CurrencyCircleDollar } from '@phosphor-icons/react';
import { averageWorkingDaysInMonth, averageWorkingHoursInDay, priceListPerHour, priceListPerMonth } from 'common/accounting/priceList';
import { CoreInstanceType, coreInstanceTypeSettings } from 'common/core/CoreInstanceConstants';
import { VMSelectItem } from 'client/pages/deployment/components/VMSelectItem';
import { Help } from 'client/components/Help';
import { Pipeline } from 'client/components/Postcard/Pipeline';
import { RadioGroup } from 'client/components/RadioGroup';
import { Stack, StackProps } from 'client/components/Stack';
import { Flip, Typography } from 'client/components/Typography';

const longCurrencyFormatter = new Intl.NumberFormat('fi-FI', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 3,
});

const shortCurrencyFormatter = new Intl.NumberFormat('fi-FI', {
  style: 'currency',
  currency: 'EUR',
});

const formatPrice = (value: number, long = true) =>
  value === 0 ? 'Free' : (long ? longCurrencyFormatter : shortCurrencyFormatter).format(value);

export interface VMSelectProps extends Partial<Omit<StackProps, 'value' | 'onChange'>> {
  value: CoreInstanceType | null;
  showDevWarning?: boolean;
  onChange: (coreInstanceTypeID: CoreInstanceType) => void;
}

export const VMSelect = ({ value, showDevWarning = false, onChange, ...other }: VMSelectProps) => {
  const theme = useTheme();

  const coreSizeItems = [...coreInstanceTypeSettings];

  const coreInstanceTypeID = value || CoreInstanceType.SMALL;

  const selectedItem = coreInstanceTypeSettings.find((item) => item.coreInstanceTypeID === coreInstanceTypeID);

  const handleChange = useCallback((value: string) => onChange(parseInt(value) as CoreInstanceType), [onChange]);

  // const minimumRam = useProjectState((state) => state.document?.templateConfiguration.requirements?.ramGiB);
  // let [recommendedMemoryGiBProd, recommendedMemoryGiBDev] = [4, 8];
  // if (minimumRam) {
  //   /**
  //    * RAM recommendations based on production RAM (`minimumRam`):
  //    *
  //    * | Production RAM (GiB) | Recommended Development RAM (GiB) |
  //    * |----------------------|-----------------------------------|
  //    * | < 4                  | 8                                 |
  //    * | < 8                  | 16                                |
  //    * | >= 8                 | Production RAM * 2                |
  //    *
  //    * This function calculates the recommended memory for development environments
  //    * based on the given production environment RAM (`minimumRam`).
  //    *
  //    */
  //   const developmentRamRecommendation = minimumRam < 4 ? 8 : minimumRam < 8 ? 16 : minimumRam * 2;
  //   [recommendedMemoryGiBProd, recommendedMemoryGiBDev] = [minimumRam, developmentRamRecommendation];
  // }

  // TODO: if I refresh deployment/create page, recommendations doesnt work anymore... why?
  // const ensureFirstSupportedOption = useCallback(() => {
  //   if (!minimumRam) return;
  //   const isSupported = (instance: CoreInstanceTypeSettings) => {
  //     return instance.memoryGiB >= recommendedMemoryGiBProd;
  //   };
  //   if (selectedItem && !isSupported(selectedItem)) {
  //     const firstSupported = coreSizeItems.find((i) => isSupported(i))?.coreInstanceTypeID.toString();
  //     if (firstSupported) handleChange(firstSupported);
  //   }
  // }, [handleChange, selectedItem, coreInstanceTypeID]);

  // ensureFirstSupportedOption();

  const items = useMemo(
    () =>
      coreSizeItems.map((item) => (
        <VMSelectItem
          key={item.coreInstanceTypeID}
          item={item}
          // isUnsupported={!!minimumRam && item.memoryGiB < recommendedMemoryGiBProd}
          // recommendedMemoryGiBProd={recommendedMemoryGiBProd}
          // recommendedMemoryGiBDev={recommendedMemoryGiBDev}
          showDevWarning={showDevWarning}
        />
      )),
    [coreSizeItems, showDevWarning]
  );

  const pricePerHour = priceListPerHour.coreInstancePrice[coreInstanceTypeID];
  const pricePerMonth = priceListPerMonth.coreInstancePrice[coreInstanceTypeID];

  return (
    <Stack gap={0} {...other}>
      <RadioGroup value={`${coreInstanceTypeID}`} items={items} onChange={handleChange} css={selectStyle} />
      {/* <Slider /> */}
      <section css={estimateStyle}>
        <Pipeline color={theme.color.primary[60]} css={pipelineStyle} />
        <div>
          <div>
            <Flip variant="h5">{formatPrice(pricePerHour * averageWorkingDaysInMonth * averageWorkingHoursInDay, false)}</Flip>
            <Typography variant="body2">/ month</Typography>
          </div>
          <Typography variant="body2">
            <CurrencyCircleDollar weight="bold" />
            Estimated cost for development
          </Typography>
        </div>
        <hr />
        <div>
          <div>
            <Flip variant="h5">{formatPrice(pricePerMonth, false)}</Flip>
            <Typography variant="body2">/ month</Typography>
          </div>
          <Typography variant="body2">
            <CurrencyCircleDollar weight="bold" />
            Estimated cost for production
          </Typography>
        </div>
      </section>
      <Help>Development cost is estimated based on an 8 hour work week. Production cost is an estimate for continuous operation.</Help>
      <Help style={{ marginTop: -10 }}>
        You will not be charged for the hours your deployment is stopped. This can be used to cut costs on deployments that don't need to be
        constantly available.
      </Help>
    </Stack>
  );
};

const selectStyle = css`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 3px;
`;

const estimateStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  margin-top: 16px;
  border: 2px solid ${theme.color.primary[100]};
  border-radius: ${theme.borderRadius}px;
  overflow: hidden;

  > div {
    flex: 1;
    padding: 24px;
    z-index: 1;

    > div {
      display: flex;
      align-items: flex-end;
      gap: 8px;
      background-color: ${transparentize(0.5, theme.color.background)};

      box-shadow: ${transparentize(0, theme.color.background)} 0px 0px 32px;

      > span {
        transform: translateY(2px);
      }

      > p {
        transform: translateY(-4px);
      }
    }

    > p {
      display: flex;
      align-items: center;
      gap: 3px;

      > svg {
        transform: translateY(1px);
      }
    }
  }

  > hr {
    display: block;
    margin: 0;
    width: 2px;
    border: none;
    background-color: ${theme.color.primary[100]};
    z-index: 1;
  }
`;

const pipelineStyle = css`
  position: absolute;
  width: 1024px;
  transform: translate(-240px, -140px) scaleX(-1);
`;
